/**
 * CARs per month, stacked by category family. Clicking a month selects it.
 *
 * Five fixed series rather than the sixteen categories: sixteen is unreadable
 * stacked, and no legend that long is worth reading. Order and colour come from
 * `./families`, which the breakdown table reads too, so the two cannot disagree
 * about which colour means what.
 *
 * Hand-written SVG for the same reason `MonthColumnChart` is: the mark is the
 * hit target, and the portal's ECharts wrapper had to reimplement that with a
 * zrender plot-band handler. This is the second stacked-period chart in the
 * product (`incoming-product/FrozenTrendChart`); they are deliberately not
 * shared, because that one carries dynamic top-N-plus-other bucketing this one
 * does not need, and generalising it would put a working report at risk to save
 * a file. If a third appears, that is the moment to extract one.
 */

import { useState } from 'react'

import { monthLabel, monthShort } from '../shared/format'
import { useElementWidth } from '../shared/useElementWidth'

import { FAMILIES } from './families'

const HEIGHT = 260
const PAD = { top: 16, right: 12, bottom: 34, left: 42 }
const MAX_BAR = 30

export interface FamilyPoint {
  month: string
  family: string
  count: number
}

interface Props {
  rows: readonly FamilyPoint[]
  selectedMonth: string | null
  onSelectMonth: (month: string) => void
  emptyLabel?: string
}

function ticksFor(max: number): number[] {
  const raw = Math.max(1, max) / 4
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? magnitude * 10
  const ticks: number[] = []
  for (let value = 0; value <= max + step * 0.001; value += step) ticks.push(value)
  return ticks
}

function n(value: number): string {
  return String(value)
}

export function CategoryStackChart({ rows, selectedMonth, onSelectMonth, emptyLabel }: Props) {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const [hovered, setHovered] = useState<{ month: string; index: number } | null>(null)

  const months = [...new Set(rows.map((r) => r.month))].sort()
  const byMonth = new Map<string, Map<string, number>>()
  for (const row of rows) {
    const bucket = byMonth.get(row.month) ?? new Map<string, number>()
    bucket.set(row.family, (bucket.get(row.family) ?? 0) + row.count)
    byMonth.set(row.month, bucket)
  }
  // Only families actually present get a legend entry -- an empty swatch is a
  // question the reader has to answer for themselves.
  const present = FAMILIES.filter((f) => rows.some((r) => r.family === f.key && r.count > 0))
  const totals = months.map((m) =>
    [...(byMonth.get(m)?.values() ?? [])].reduce((a, b) => a + b, 0),
  )
  const max = Math.max(1, ...totals)
  const ticks = ticksFor(max)
  const top = ticks[ticks.length - 1] ?? max

  if (months.length === 0) {
    return <div className="state">{emptyLabel ?? 'No CARs for these filters.'}</div>
  }

  const plotWidth = Math.max(0, width - PAD.left - PAD.right)
  const plotHeight = HEIGHT - PAD.top - PAD.bottom
  const band = months.length > 0 ? plotWidth / months.length : 0
  const barWidth = Math.min(MAX_BAR, band * 0.62)
  const yOf = (value: number) => PAD.top + plotHeight - (value / top) * plotHeight
  const labelEvery = Math.max(1, Math.ceil(months.length / Math.max(1, Math.floor(plotWidth / 46))))

  // The hovered column's breakdown, in the families' declared order so the box
  // reads top-to-bottom the same way the stack does.
  const hoveredBreakdown =
    hovered === null
      ? []
      : FAMILIES.map((family) => ({
          key: family.key,
          color: family.color,
          value: byMonth.get(hovered.month)?.get(family.key) ?? 0,
        })).filter((entry) => entry.value > 0)
  const hoveredTotal = hoveredBreakdown.reduce((sum, entry) => sum + entry.value, 0)

  return (
    <div ref={ref} className="stack-chart">
      <ul className="stack-legend">
        {present.map((family) => (
          <li key={family.key}>
            <span className="stack-swatch" style={{ background: family.color }} />
            {family.key}
          </li>
        ))}
      </ul>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label="CARs per month by category">
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={PAD.left + plotWidth}
                y1={yOf(tick)}
                y2={yOf(tick)}
                stroke="var(--gridline)"
              />
              <text
                x={PAD.left - 8}
                y={yOf(tick) + 4}
                textAnchor="end"
                fontSize={11}
                fill="var(--text-muted)"
              >
                {n(tick)}
              </text>
            </g>
          ))}
          {months.map((month, index) => {
            const bucket = byMonth.get(month)
            const isSelected = month === selectedMonth
            const x = PAD.left + band * index + (band - barWidth) / 2
            let cursorY = PAD.top + plotHeight
            const total = totals[index] ?? 0
            return (
              <g key={month}>
                {FAMILIES.map((family) => {
                  const value = bucket?.get(family.key) ?? 0
                  if (value <= 0) return null
                  const height = (value / top) * plotHeight
                  cursorY -= height
                  return (
                    <rect
                      key={family.key}
                      x={x}
                      y={cursorY}
                      width={barWidth}
                      height={height}
                      fill={family.color}
                      opacity={isSelected || hovered?.month === month ? 1 : 0.82}
                    />
                  )
                })}
                {/* A full-height band, so the whole column is clickable rather
                    than only the stacked segments. */}
                <rect
                  x={PAD.left + band * index}
                  y={PAD.top}
                  width={band}
                  height={plotHeight}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => {
                    setHovered({ month, index })
                  }}
                  onMouseLeave={() => {
                    setHovered(null)
                  }}
                  onClick={() => {
                    onSelectMonth(month)
                  }}
                />
                {isSelected && total > 0 && (
                  <text
                    x={PAD.left + band * index + band / 2}
                    y={yOf(total) - 6}
                    textAnchor="middle"
                    fontSize={12}
                    fontWeight={600}
                    fill="var(--text-primary)"
                  >
                    {n(total)}
                  </text>
                )}
                {(index % labelEvery === 0 || isSelected) && (
                  <text
                    x={PAD.left + band * index + band / 2}
                    y={HEIGHT - 12}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={isSelected ? 600 : 400}
                    fill={isSelected ? 'var(--text-primary)' : 'var(--text-muted)'}
                  >
                    {monthShort(month)}
                  </text>
                )}
              </g>
            )
          })}
        </svg>
      )}
      {hovered !== null && (
        <div
          className="stack-tooltip"
          // Anchored to the hovered column and flipped near the right edge, so
          // the box never leaves the card. `pointer-events: none` in the CSS:
          // a box under the cursor would steal the mouseleave and the tooltip
          // would stick.
          style={{
            left: PAD.left + band * hovered.index + band / 2,
            transform:
              PAD.left + band * hovered.index + band / 2 > width - 150
                ? 'translate(-100%, 0)'
                : 'translate(-50%, 0)',
          }}
          role="status"
        >
          <p className="stack-tooltip-head">
            <span>{monthLabel(hovered.month)}</span>
            <strong>
              {n(hoveredTotal)} {hoveredTotal === 1 ? 'CAR' : 'CARs'}
            </strong>
          </p>
          <ul>
            {hoveredBreakdown.map((entry) => (
              <li key={entry.key}>
                <span className="stack-swatch" style={{ background: entry.color }} />
                <span className="stack-tooltip-name">{entry.key}</span>
                <span className="stack-tooltip-value">{n(entry.value)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
